'use client';

import { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore } from 'react';

const subscribeToMount = () => () => undefined;
const getClientMountSnapshot = () => true;
const getServerMountSnapshot = () => false;

export function useVerticalScrollCarousel(
  containerRef: React.RefObject<HTMLDivElement | null>,
  stickyContainerRef: React.RefObject<HTMLDivElement | null>,
  initialHeight: number,
  stepLength: number,
  stepWeights?: number[] // length = stepLength - 1; relative scroll weight per transition
) {
  const isMounted = useSyncExternalStore(
    subscribeToMount,
    getClientMountSnapshot,
    getServerMountSnapshot
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stickyContainerHeight, setStickyContainerHeight] = useState(initialHeight);
  const rafRef = useRef<number | null>(null);

  // Cumulative weight offsets, recomputed only when weights/stepLength change
  const meta = useMemo(() => {
    const w =
      stepWeights?.length === stepLength - 1
        ? stepWeights
        : Array(Math.max(0, stepLength - 1)).fill(1);
    const cumulative = [0];
    for (const weight of w) cumulative.push(cumulative[cumulative.length - 1] + weight);
    return { cumulative, totalWeight: cumulative[cumulative.length - 1], weights: w };
  }, [stepWeights, stepLength]);

  // Keep ref in sync so the scroll callback always reads fresh values without
  // being listed as a dependency (avoids re-attaching the scroll listener).
  const metaRef = useRef(meta);

  useEffect(() => {
    metaRef.current = meta;
  }, [meta]);

  const updateFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const scrolled = -rect.top;
    const { cumulative, totalWeight, weights } = metaRef.current;
    const totalScrollable = stickyContainerHeight * totalWeight;

    if (scrolled <= 0) {
      setActiveIndex(0);
      setProgress(0);
    } else if (scrolled >= totalScrollable) {
      setActiveIndex(stepLength - 1);
      setProgress(100);
    } else {
      const normalizedScroll = scrolled / stickyContainerHeight;
      // Find the transition bucket
      let idx = 0;
      while (idx < cumulative.length - 2 && cumulative[idx + 1] <= normalizedScroll) {
        idx++;
      }
      const stepWeight = weights[idx] ?? 1;
      const prog = ((normalizedScroll - cumulative[idx]) / stepWeight) * 100;
      setActiveIndex(idx);
      setProgress(prog);
    }
  }, [containerRef, stickyContainerHeight, stepLength]);

  useEffect(() => {
    const scheduleUpdate = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updateFromScroll();
      });
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    scheduleUpdate();

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [updateFromScroll]);

  useEffect(() => {
    if (stickyContainerRef.current) {
      setStickyContainerHeight(stickyContainerRef.current.offsetHeight);
    }
  }, [stickyContainerRef]);

  const handleItemClick = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const { cumulative } = metaRef.current;
    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const targetScroll = Math.ceil(
      containerTop + (cumulative[index] ?? index) * stickyContainerHeight
    );
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  };

  return {
    activeIndex,
    progress,
    containerRef,
    handleItemClick,
    stickyContainerHeight,
    totalHeight:
      stickyContainerHeight * meta.totalWeight +
      (isMounted && typeof window !== 'undefined' ? window.innerHeight : stickyContainerHeight),
  };
}
